import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../setup/Icon";
import { ADMIN_PAGES, type AdminPageLink } from "../../shared/admin-pages";
import styles from "./AdminDirectory.module.scss";

// The Admin page's directory: every admin / dev page (shared/admin-pages.ts) as
// a card per thing-you're-working-on. Type to filter (Enter opens the first
// hit); the last few pages you opened sit on top, since in practice it's the
// same three or four every day.

const RECENT_KEY = "ar.admin.recent";
const RECENT_MAX = 4;

function loadRecent(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
    return Array.isArray(v) ? v.filter((x) => typeof x === "string").slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}
function saveRecent(paths: string[]): void {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(paths));
  } catch { /* private mode: no recents, no harm */ }
}

const ALL: (AdminPageLink & { color: string })[] = ADMIN_PAGES.flatMap((g) => g.pages.map((p) => ({ ...p, color: g.color })));

export default function AdminDirectory() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [recent, setRecent] = useState<string[]>(loadRecent);
  const inputRef = useRef<HTMLInputElement>(null);

  const q = query.trim().toLowerCase();
  const matches = (p: AdminPageLink, groupTitle: string) =>
    !q || q.split(/\s+/).every((w) => (p.label + " " + p.desc + " " + p.path + " " + groupTitle).toLowerCase().includes(w));
  const groups = useMemo(
    () => ADMIN_PAGES.map((g) => ({ ...g, pages: g.pages.filter((p) => matches(p, g.title)) })).filter((g) => g.pages.length),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [q],
  );
  const hitCount = groups.reduce((n, g) => n + g.pages.length, 0);

  function open(p: AdminPageLink) {
    // a tool further down this page: scroll to it
    if (p.path.startsWith("#")) {
      document.getElementById(p.path.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    // somewhere outside the app (the growth board runs on its own localhost
    // port): a new tab, and it never joins Recent, since it is not our page.
    if (/^https?:\/\//.test(p.path)) {
      window.open(p.path, "_blank", "noopener,noreferrer");
      return;
    }
    const next = [p.path, ...recent.filter((x) => x !== p.path)].slice(0, RECENT_MAX);
    setRecent(next);
    saveRecent(next);
    navigate(p.path);
  }

  const recentPages = recent.map((path) => ALL.find((p) => p.path === path)).filter(Boolean) as typeof ALL;

  return (
    <div className={styles.directory}>
      <div className={styles.searchRow}>
        <label className={styles.search}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7" /><line x1="16.5" y1="16.5" x2="21" y2="21" />
          </svg>
          <input
            ref={inputRef}
            type="search"
            value={query}
            placeholder="Find a page: bots, lag, flags, /debug…"
            aria-label="Find an admin page"
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && groups.length) open(groups[0].pages[0]);
              if (e.key === "Escape" && query) { e.stopPropagation(); setQuery(""); }
            }}
          />
          {q && <span className={styles.searchCount}>{hitCount} {hitCount === 1 ? "page" : "pages"} · Enter opens the first</span>}
        </label>
      </div>

      {!q && recentPages.length > 0 && (
        <div className={styles.recent}>
          <span className={styles.recentLabel}>Recent</span>
          {recentPages.map((p) => (
            <button key={p.path} className={styles.recentChip} style={{ "--accent": p.color } as React.CSSProperties} onClick={() => open(p)} title={p.desc}>
              {p.label}
            </button>
          ))}
        </div>
      )}

      {groups.length === 0 && <p className={styles.empty}>Nothing matches “{query}”.</p>}

      <div className={styles.grid}>
        {groups.map((g) => (
          <section key={g.id} className={styles.group} style={{ "--accent": g.color } as React.CSSProperties}>
            <header className={styles.groupHead}>
              <span className={styles.groupIcon}><Icon name={g.icon} size={18} /></span>
              <div>
                <h2>{g.title}</h2>
                <p>{g.about}</p>
              </div>
            </header>
            <ul className={styles.links}>
              {g.pages.map((p) => (
                <li key={p.path}>
                  <button className={styles.link} onClick={() => open(p)}>
                    <span className={styles.linkText}>
                      <strong>{p.label}</strong>
                      <span>{p.desc}</span>
                    </span>
                    <span className={styles.linkGo} aria-hidden="true">{p.path.startsWith("#") ? "↓" : /^https?:\/\//.test(p.path) ? "↗" : "→"}</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}
